import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Typography, Grid, Card, CardContent, Avatar, Button,
  TextField, Select, MenuItem, FormControl, InputLabel, Chip, Rating,
  Slider, IconButton, Divider, CircularProgress, Paper, InputAdornment,
  Collapse, Skeleton
} from '@mui/material';
import {
  Search as SearchIcon, FilterList as FilterIcon, Star as StarIcon,
  Language as LanguageIcon, FavoriteBorder, Favorite,
  ExpandMore, ExpandLess, School as SchoolIcon
} from '@mui/icons-material';
import { searchTeachers } from '../../api/marketplace';
import { getCurrentLanguage } from '../../utils/navigation';
import { useAuth } from '../../store/authStore';

const LANGUAGE_OPTIONS = [
  'Arabic', 'English', 'French', 'Spanish', 'German', 'Italian',
  'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Turkish', 'Russian'
];

const SearchTeachers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const lang = getCurrentLanguage();

  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    language: '',
    minPrice: 0,
    maxPrice: 100,
    country: '',
    specialty: '',
    nativeSpeaker: '',
    sortBy: 'top_picks'
  });

  useEffect(() => {
    loadTeachers();
  }, []);

  const loadTeachers = async () => {
    setLoading(true);
    try {
      const activeFilters = {};
      if (filters.language) activeFilters.language = filters.language;
      if (filters.minPrice > 0) activeFilters.minPrice = filters.minPrice;
      if (filters.maxPrice < 100) activeFilters.maxPrice = filters.maxPrice;
      if (filters.country) activeFilters.country = filters.country;
      if (filters.specialty) activeFilters.specialty = filters.specialty;
      if (filters.nativeSpeaker) activeFilters.nativeSpeaker = filters.nativeSpeaker;
      activeFilters.sortBy = filters.sortBy;

      const results = await searchTeachers(activeFilters);
      setTeachers(results);
    } catch (error) {
      console.error('Error loading teachers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => loadTeachers();

  const handleFilterChange = (field) => (e) => {
    setFilters(prev => ({ ...prev, [field]: e.target.value }));
  };

  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'EUR': return '€';
      case 'MAD': return 'د.م.';
      case 'GBP': return '£';
      default: return '$';
    }
  };

  const TeacherCard = ({ teacher }) => (
    <Card
      sx={{
        mb: 2, cursor: 'pointer', transition: 'all 0.2s',
        '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
        border: '1px solid #e0e0e0'
      }}
      onClick={() => navigate(`/${lang}/student/teacher/${teacher.id}`)}
    >
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Photo */}
          <Grid item xs={12} sm={2}>
            <Avatar
              src={teacher.photoURL}
              sx={{ width: 100, height: 100, mx: 'auto', fontSize: 40 }}
            >
              {teacher.displayName?.charAt(0)}
            </Avatar>
          </Grid>

          {/* Info */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" fontWeight={700}>
                {teacher.displayName}
              </Typography>
              {teacher.isNew && (
                <Chip label="New" size="small" color="info" />
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <SchoolIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              {teacher.specialties?.slice(0, 3).join(' · ') || 'General'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <LanguageIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              Speaks {teacher.languages?.join(', ')}
              {teacher.nativeLanguages?.length > 0 && (
                <span> ({teacher.nativeLanguages.join(', ')} - Native)</span>
              )}
            </Typography>

            <Typography
              variant="body2" color="text.secondary"
              sx={{
                display: '-webkit-box', WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical', overflow: 'hidden'
              }}
            >
              {teacher.bio || teacher.description}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
              {teacher.averageRating > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Rating value={teacher.averageRating} readOnly size="small" precision={0.5} />
                  <Typography variant="body2" fontWeight={600}>
                    {teacher.averageRating}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary">
                {teacher.totalStudents || 0} students
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teacher.totalLessons || 0} lessons
              </Typography>
            </Box>
          </Grid>

          {/* Price & Actions */}
          <Grid item xs={12} sm={4} sx={{ textAlign: { sm: 'right' } }}>
            <Typography variant="h5" fontWeight={700} color="primary.main">
              {getCurrencySymbol(teacher.currency)}{teacher.pricePerHour}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {teacher.lessonDuration || 50}-min lesson
            </Typography>

            <Button
              variant="contained" fullWidth
              sx={{
                mb: 1, bgcolor: '#F5A623', color: '#fff',
                '&:hover': { bgcolor: '#e09500' },
                fontWeight: 600, py: 1.2
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/${lang}/student/pay/${teacher.id}`);
              }}
            >
              Book trial lesson
            </Button>
            <Button
              variant="outlined" fullWidth
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/${lang}/student/teacher/${teacher.id}`);
              }}
            >
              Send message
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pt: 10, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Search Bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>I want to learn</InputLabel>
                <Select value={filters.language} onChange={handleFilterChange('language')} label="I want to learn">
                  <MenuItem value="">All Languages</MenuItem>
                  {LANGUAGE_OPTIONS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">Price per lesson</Typography>
              <Slider
                value={[filters.minPrice, filters.maxPrice]}
                onChange={(_, v) => setFilters(prev => ({ ...prev, minPrice: v[0], maxPrice: v[1] }))}
                valueLabelDisplay="auto" min={0} max={200}
                valueLabelFormat={(v) => `$${v}`}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort by</InputLabel>
                <Select value={filters.sortBy} onChange={handleFilterChange('sortBy')} label="Sort by">
                  <MenuItem value="top_picks">Our top picks</MenuItem>
                  <MenuItem value="price_low">Price: Low to High</MenuItem>
                  <MenuItem value="price_high">Price: High to Low</MenuItem>
                  <MenuItem value="rating">Highest rated</MenuItem>
                  <MenuItem value="lessons">Most popular</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined" fullWidth
                startIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setShowFilters(!showFilters)}
              >
                More filters
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button variant="contained" fullWidth onClick={handleSearch} startIcon={<SearchIcon />}>
                Search
              </Button>
            </Grid>
          </Grid>

          {/* Extended Filters */}
          <Collapse in={showFilters}>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Specialty</InputLabel>
                  <Select value={filters.specialty} onChange={handleFilterChange('specialty')} label="Specialty">
                    <MenuItem value="">All</MenuItem>
                    {['Conversational', 'Business', 'Test Preparation', 'Grammar', 'Kids', 'Academic'].map(s => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Native speaker</InputLabel>
                  <Select value={filters.nativeSpeaker} onChange={handleFilterChange('nativeSpeaker')} label="Native speaker">
                    <MenuItem value="">Any</MenuItem>
                    {LANGUAGE_OPTIONS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth size="small" label="Country"
                  value={filters.country} onChange={handleFilterChange('country')}
                  placeholder="Teacher's country"
                />
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {/* Results Header */}
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          {loading ? 'Searching...' : `${teachers.length} teachers that match your needs`}
        </Typography>

        {/* Teacher Cards */}
        {loading ? (
          [...Array(3)].map((_, i) => (
            <Card key={i} sx={{ mb: 2, p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={2}><Skeleton variant="circular" width={100} height={100} /></Grid>
                <Grid item xs={6}>
                  <Skeleton width="60%" height={32} />
                  <Skeleton width="40%" />
                  <Skeleton width="80%" />
                  <Skeleton width="50%" />
                </Grid>
                <Grid item xs={4}>
                  <Skeleton width="50%" height={40} sx={{ ml: 'auto' }} />
                  <Skeleton height={48} sx={{ mt: 2 }} />
                  <Skeleton height={40} />
                </Grid>
              </Grid>
            </Card>
          ))
        ) : teachers.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No teachers found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or search for a different language
            </Typography>
          </Paper>
        ) : (
          teachers.map(teacher => <TeacherCard key={teacher.id} teacher={teacher} />)
        )}
      </Container>
    </Box>
  );
};

export default SearchTeachers;
